import type { ResumableSession } from "~/api/dialer"
import type { BurstStatus } from "~/containers/people-os/burst-status"

export type DialOutcome = "connected" | "unconnected" | "cancelled"

export interface IPersonOutcome {
  outcome: DialOutcome
  reason: "no_answer" | "rejected" | null
  durationSeconds: number | null
}

export type OutcomeGroupKey =
  | "unconnected"
  | "connected"
  | "cancelled"
  | "live"
  | "upcoming"

export const OUTCOME_GROUP_ORDER: OutcomeGroupKey[] = [
  "unconnected",
  "connected",
  "cancelled",
  "live",
  "upcoming"
]

// Keys outcomes by String(person_id). A called person with a null outcome
// reads as unconnected / no answer.
export function outcomesFromCalledPeople(
  rows: ResumableSession["called_people"]
): Map<string, IPersonOutcome> {
  const map = new Map<string, IPersonOutcome>()
  for (const r of rows) {
    if (!r.outcome) {
      map.set(String(r.person_id), {
        outcome: "unconnected",
        reason: "no_answer",
        durationSeconds: null
      })
      continue
    }
    map.set(String(r.person_id), {
      outcome: r.outcome,
      reason: r.outcome_reason,
      durationSeconds: r.duration_seconds
    })
  }
  return map
}

// Ringing has no outcome yet, so it maps to null.
export function outcomeFromBurstStatus(s: BurstStatus): IPersonOutcome | null {
  switch (s) {
    case "noanswer":
      return {
        outcome: "unconnected",
        reason: "no_answer",
        durationSeconds: null
      }
    case "rejected":
      return {
        outcome: "unconnected",
        reason: "rejected",
        durationSeconds: null
      }
    case "cancelled":
      return { outcome: "cancelled", reason: null, durationSeconds: null }
    case "connected":
      return { outcome: "connected", reason: null, durationSeconds: null }
    default:
      return null
  }
}

// Buckets rows into non-empty groups in OUTCOME_GROUP_ORDER. Live ids take
// precedence over outcomes; without parallel dialing, cancelled counts as unconnected.
export function groupRows<T extends { id: string | number }>(
  rows: T[],
  outcomes: Map<string, IPersonOutcome>,
  liveIds: Set<string>,
  parallel: boolean
): { key: OutcomeGroupKey; rows: T[] }[] {
  const buckets = new Map<OutcomeGroupKey, T[]>()
  for (const row of rows) {
    const id = String(row.id)
    let key: OutcomeGroupKey
    if (liveIds.has(id)) key = "live"
    else {
      const o = outcomes.get(id)?.outcome
      if (!o) key = "upcoming"
      else if (o === "cancelled" && !parallel) key = "unconnected"
      else key = o
    }
    const list = buckets.get(key)
    if (list) list.push(row)
    else buckets.set(key, [row])
  }
  return OUTCOME_GROUP_ORDER.filter((k) => buckets.has(k)).map((key) => ({
    key,
    rows: buckets.get(key) as T[]
  }))
}

// Seeded outcomes overlaid with every terminal status from this run.
export function mergeOutcomes(
  seeded: Map<string, IPersonOutcome>,
  statuses: Record<string, BurstStatus>
): Map<string, IPersonOutcome> {
  const merged = new Map(seeded)
  for (const [id, status] of Object.entries(statuses)) {
    const o = outcomeFromBurstStatus(status)
    if (o) merged.set(id, o)
  }
  return merged
}

export type StatusChipKind =
  | "calling"
  | "oncall"
  | "connected"
  | "noanswer"
  | "rejected"
  | "cancelled"
  | "upnext"
  | "upcoming"

// Picks the status chip for a row: on call, then ringing, then the outcome,
// then queued ("Up next") or "Upcoming".
export function statusChipKind({
  ringing,
  onCall,
  outcome,
  queued
}: {
  ringing: boolean
  onCall: boolean
  outcome: IPersonOutcome | undefined
  queued: boolean
}): StatusChipKind {
  if (onCall) return "oncall"
  if (ringing) return "calling"
  if (outcome?.outcome === "connected") return "connected"
  if (outcome?.outcome === "cancelled") return "cancelled"
  if (outcome?.outcome === "unconnected") {
    return outcome.reason === "rejected" ? "rejected" : "noanswer"
  }
  return queued ? "upnext" : "upcoming"
}

// Formats seconds as mm:ss.
export function formatMmSs(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`
}

export interface IOutcomeTally {
  connected: number
  unconnected: number
  cancelled: number
}

// Counts the given people by outcome. Without parallel dialing, cancelled
// counts as unconnected, as in groupRows.
export function tallyOutcomes(
  ids: string[],
  outcomes: Map<string, IPersonOutcome>,
  parallel: boolean
): IOutcomeTally {
  const tally: IOutcomeTally = { connected: 0, unconnected: 0, cancelled: 0 }
  for (const id of ids) {
    const o = outcomes.get(id)?.outcome
    if (!o) continue
    if (o === "cancelled" && !parallel) tally.unconnected += 1
    else tally[o] += 1
  }
  return tally
}

// How many queued people have a terminal status from this run.
export function countDialedInRun(
  queueIds: string[],
  statuses: Record<string, BurstStatus>
): number {
  let n = 0
  for (const id of queueIds) {
    const status = statuses[id]
    if (status && outcomeFromBurstStatus(status) !== null) n += 1
  }
  return n
}
